import { useMutation } from '@tanstack/react-query';
import { ApiResponse } from 'apisauce';
import useHandleAppException, {
  type AppExceptionResponse,
} from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import snackbarStore from '../../../stores/Snackbar.store';
import {
  bulkActivateCatalogs,
  bulkAddClassificationPermissions,
  bulkAddCustomers,
  bulkAddSalesRepresentatives,
  bulkDeactivateCatalogs,
  bulkDeleteCatalogs,
} from './catalogsBulk.repository';
import {
  CatalogBulkAddClassificationsRequest,
  CatalogBulkAddCustomersRequest,
  CatalogBulkAddSalesRepsRequest,
  CatalogBulkBaseRequest,
} from './catalogsBulk.repository.types';

type BulkRequest =
  | CatalogBulkBaseRequest
  | CatalogBulkAddCustomersRequest
  | CatalogBulkAddSalesRepsRequest
  | CatalogBulkAddClassificationsRequest;

export const catalogBulkKeys = {
  activate: ['catalogs', 'bulk', 'activate'] as const,
  deactivate: ['catalogs', 'bulk', 'deactivate'] as const,
  delete: ['catalogs', 'bulk', 'delete'] as const,
  addCustomers: ['catalogs', 'bulk', 'add-customers'] as const,
  addSalesRepresentatives: ['catalogs', 'bulk', 'add-sales-representatives'] as const,
  addClassificationPermissions: ['catalogs', 'bulk', 'add-classification-permissions'] as const,
};

function createBulkMutationHook<TData extends BulkRequest>(
  mutationKey: readonly string[],
  requestFn: (data: TData) => Promise<ApiResponse<void>>,
  successMessageKey: LangEnum,
) {
  return () => {
    const { translate } = useTranslation();
    const { handleErrorResponse } = useHandleAppException();

    return useMutation({
      mutationKey,
      mutationFn: async (data: TData) => {
        const response = await requestFn(data);
        if (!response.ok) {
          throw response.originalError;
        }
      },
      onSuccess: () => {
        snackbarStore.handleMessage({ type: 'success', message: translate(successMessageKey) });
      },
      onError: (error: unknown) => {
        const apiError = error as { response?: AppExceptionResponse } | undefined;
        const message = apiError?.response ? handleErrorResponse(apiError.response) : '';
        snackbarStore.handleMessage({ type: 'error', message });
      },
    });
  };
}

export const useBulkActivateCatalogs = createBulkMutationHook(
  catalogBulkKeys.activate,
  bulkActivateCatalogs,
  LangEnum.CATALOGS_BULK_ACTIVATE_PROCESSING,
);

export const useBulkDeactivateCatalogs = createBulkMutationHook(
  catalogBulkKeys.deactivate,
  bulkDeactivateCatalogs,
  LangEnum.CATALOGS_BULK_DEACTIVATE_PROCESSING,
);

export const useBulkDeleteCatalogs = createBulkMutationHook(
  catalogBulkKeys.delete,
  bulkDeleteCatalogs,
  LangEnum.CATALOGS_BULK_DELETE_PROCESSING,
);

export const useBulkAddCustomers = createBulkMutationHook(
  catalogBulkKeys.addCustomers,
  bulkAddCustomers,
  LangEnum.CATALOGS_BULK_ADD_CUSTOMERS_PROCESSING,
);

export const useBulkAddSalesRepresentatives = createBulkMutationHook(
  catalogBulkKeys.addSalesRepresentatives,
  bulkAddSalesRepresentatives,
  LangEnum.CATALOGS_BULK_ADD_SALES_REPS_PROCESSING,
);

export const useBulkAddClassificationPermissions = createBulkMutationHook(
  catalogBulkKeys.addClassificationPermissions,
  bulkAddClassificationPermissions,
  LangEnum.CATALOGS_BULK_ADD_CLASSIFICATIONS_PROCESSING,
);
